import { createContext, useCallback, useContext } from 'react';

import type { DashboardQuery } from '@/data-sources/contract';

import { useAppliedFilters } from '../data/AppliedFiltersContext';
import { usePageMeta } from '../data/PageContext';
import { canonicalQuery } from '../filters/canonicalQuery';
import { useReportScope, withReportScope } from '../filters/useReportScope';
import type { OpenConversationsRequest } from './present';

export type OpenConversationsWith = (
  request: OpenConversationsRequest,
  query: DashboardQuery,
) => void;

/** Provided by `ConversationsProvider`; absent → rows stay inert. */
export const OpenConversationsContext = createContext<OpenConversationsWith | undefined>(undefined);

/**
 * Opens the conversations modal for a table row — a Competitors page, a
 * Community voice or community (or any chart point). The list is resolved
 * under the calling page's canonical query, frozen at the moment of the click,
 * so its count reconciles with the row that was clicked.
 *
 *   const openConversations = useOpenConversations();
 *   openConversations?.({ kind: 'page', brand: 'robi', channel: 'facebook' });
 *   openConversations?.({ kind: 'community', dimension: 'voice', value: 'gpcarehelp', label: '@gpcarehelp' });
 *
 * `undefined` outside a `ConversationsProvider` (a story, a thumbnail), which
 * leaves rows inert rather than broken.
 */
export function useOpenConversations(): ((request: OpenConversationsRequest) => void) | undefined {
  const openWith = useContext(OpenConversationsContext);
  const filters = useAppliedFilters();
  const { filtersType } = usePageMeta();
  // A custom report scopes View details to its campaign's posts, as it does its widgets.
  const dashboardId = useReportScope();
  const open = useCallback(
    (request: OpenConversationsRequest) =>
      openWith?.(request, withReportScope(canonicalQuery(filters, filtersType), dashboardId)),
    [openWith, filters, filtersType, dashboardId],
  );
  return openWith ? open : undefined;
}
